import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, Loader2 } from 'lucide-react';
import { useAuth, type User } from '../../contexts/AuthContext';
import { MAILTO_SUPPORT_HREF, SUPPORT_EMAIL } from '../../constants/publicSite';
import LandingNav from '../home/LandingNav';
import ForgotPasswordModal from './ForgotPasswordModal';
import SignInDisclaimerModal from './SignInDisclaimerModal';
import '../home/Home.css';

export default function Login({
  onBack,
  onSuccess,
  embedded,
}: {
  onBack: () => void;
  onSuccess: () => void;
  embedded?: boolean;
}) {
  const { login, completeLogin, loginError } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingUser, setPendingUser] = useState<User | null>(null);

  const forgotVariant = location.pathname.startsWith('/admin') ? 'platform' : 'workspace';

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      const loggedInUser = await login(email.trim(), password);
      const isClientAccess = loggedInUser.loginType === 'Client' || loggedInUser.role === 'client';
      if (isClientAccess) {
        setPendingUser(loggedInUser);
        setShowDisclaimer(true);
      } else {
        completeLogin(loggedInUser);
        onSuccess();
      }
    } catch (_) {
      // error shown via loginError
    } finally {
      setLoading(false);
    }
  };

  const formDisabled = !email.trim() || !password.trim() || loading;

  const handleDisclaimerAccept = () => {
    setShowDisclaimer(false);
    if (pendingUser) {
      completeLogin(pendingUser);
      setPendingUser(null);
    }
    onSuccess();
  };

  const formContent = (
    <div className={embedded ? 'w-full min-w-0' : 'w-full min-w-0 max-w-md'}>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Sign in</h1>
      <p className="text-sm text-gray-600 mb-4">
        Sign in to your ParachuteIQ workspace. Access is determined by your account role.
      </p>
      <div className={embedded ? undefined : 'bg-white rounded-xl border border-gray-200 shadow-sm p-6'}>
        {loginError && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{loginError}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full min-h-11 sm:min-h-9 box-border py-2.5 sm:py-0 px-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
            />
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-1">
              <label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                Password
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <input
              id="login-password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full min-h-11 sm:min-h-9 box-border py-2.5 sm:py-0 px-3 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={formDisabled}
            className="w-full min-h-11 sm:min-h-10 h-auto py-2.5 sm:py-0 flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-60 disabled:cursor-not-allowed text-base sm:text-sm shadow-sm touch-manipulation"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
      {!embedded && (
        <p className="mt-6 text-center text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <Link to="/" className="font-medium text-emerald-700 underline-offset-2 hover:underline">
            Request early access
          </Link>{' '}
          from the home page.
        </p>
      )}
    </div>
  );

  if (embedded) {
    return (
      <>
        {formContent}
        <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} variant={forgotVariant} />
        <SignInDisclaimerModal open={showDisclaimer} onAccept={handleDisclaimerAccept} />
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--cream)]">
      <LandingNav mode="auth" onBack={onBack} />
      <main className="flex flex-1 flex-col items-center justify-center px-4 sm:px-8 pt-[calc(var(--landing-nav-height)+1.5rem)] pb-8 min-w-0 w-full">
        {formContent}
      </main>
      <footer className="shrink-0 border-t border-slate-200/70 bg-[var(--cream)] px-4 py-6">
        <div className="mx-auto flex max-w-[1080px] flex-col items-center justify-center gap-3 text-center text-xs text-gray-500 sm:flex-row sm:flex-wrap sm:gap-x-4 sm:gap-y-1 sm:text-sm">
          <span>© {new Date().getFullYear()} ParachuteIQ</span>
          <span className="hidden text-slate-300 sm:inline" aria-hidden>
            ·
          </span>
          <Link to="/privacy" className="text-emerald-800/90 hover:underline">
            Privacy Policy
          </Link>
          <span className="hidden text-slate-300 sm:inline" aria-hidden>
            ·
          </span>
          <Link to="/terms" className="text-emerald-800/90 hover:underline">
            Terms of Use
          </Link>
          <span className="hidden text-slate-300 sm:inline" aria-hidden>
            ·
          </span>
          <a href={MAILTO_SUPPORT_HREF} className="text-emerald-800/90 hover:underline">
            Support ({SUPPORT_EMAIL})
          </a>
        </div>
      </footer>
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} variant={forgotVariant} />
      <SignInDisclaimerModal open={showDisclaimer} onAccept={handleDisclaimerAccept} />
    </div>
  );
}
